import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import {
  adminApplicationApprove,
  adminApplicationReject,
  adminApplicationRequestInfo,
  adminApplicationsList,
  adminApplicationsMeta,
  adminApplicationUpdate,
} from "./applicationsApi";
import "./Applications.css";

/**
 * Platform admin — Applications review (mirrors AdvertisingAdminPage patterns).
 */
export default function ApplicationsAdminPage() {
  const [meta, setMeta] = useState(null);
  const [items, setItems] = useState([]);
  const [selected, setSelected] = useState(null);
  const [filterType, setFilterType] = useState("");
  const [filterStatus, setFilterStatus] = useState("");
  const [query, setQuery] = useState("");
  const [reviewNotes, setReviewNotes] = useState("");
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [message, setMessage] = useState("");
  const [error, setError] = useState("");

  const load = async () => {
    setLoading(true);
    setError("");
    try {
      const [metaBody, listBody] = await Promise.all([
        adminApplicationsMeta(),
        adminApplicationsList({
          type: filterType || undefined,
          status: filterStatus || undefined,
          q: query || undefined,
        }),
      ]);
      if (metaBody && metaBody.success) setMeta(metaBody.data);
      if (listBody && listBody.success) {
        setItems((listBody.data && listBody.data.items) || []);
      } else if (listBody && listBody.error === "forbidden") {
        setError("Platform admin access required.");
      } else if (listBody && !listBody.success) {
        setError(listBody.error_messages || listBody.error || "Unable to load.");
      }
    } catch (e) {
      setError("Unable to load applications.");
    }
    setLoading(false);
  };

  useEffect(() => {
    load();
  }, [filterType, filterStatus]); // eslint-disable-line react-hooks/exhaustive-deps

  const selectItem = (item) => {
    setSelected(item);
    setReviewNotes(item.review_notes || "");
    setMessage("");
    setError("");
  };

  const runAction = async (action) => {
    if (!selected) return;
    setBusy(true);
    setMessage("");
    setError("");
    try {
      let body;
      if (action === "save") {
        body = await adminApplicationUpdate({
          application_id: selected.id,
          review_notes: reviewNotes,
        });
      } else if (action === "approve") {
        body = await adminApplicationApprove(selected.id, reviewNotes);
      } else if (action === "reject") {
        body = await adminApplicationReject(selected.id, reviewNotes);
      } else if (action === "info") {
        body = await adminApplicationRequestInfo(selected.id, reviewNotes);
      }
      if (body && body.success && body.data) {
        setSelected(body.data);
        setReviewNotes(body.data.review_notes || "");
        setMessage(
          action === "approve"
            ? "Approved and entity provisioned."
            : action === "reject"
            ? "Application rejected."
            : action === "info"
            ? "Information requested."
            : "Notes saved."
        );
        await load();
      } else {
        setError(
          (body && (body.error_messages || body.error)) || "Action failed."
        );
      }
    } catch (e) {
      setError("Action failed.");
    }
    setBusy(false);
  };

  const types = (meta && meta.types) || {};
  const statuses = (meta && meta.statuses) || {};
  const reviewable =
    selected &&
    (selected.status === "pending" || selected.status === "needs_information");

  return (
    <div className="apps-admin">
      <header className="apps-admin__header">
        <h1>Applications</h1>
        <nav>
          <Link to="/studio/applications">Applications</Link>
          {" · "}
          <Link to="/studio/advertising">Advertising</Link>
          {" · "}
          <Link to="/ops">Ops</Link>
        </nav>
      </header>

      {message ? <p className="apps-admin__message">{message}</p> : null}
      {error ? <p className="apps-admin__error">{error}</p> : null}

      <div className="apps-admin__filters">
        <select
          className="form-control"
          value={filterType}
          onChange={(e) => setFilterType(e.target.value)}
          aria-label="Filter by type"
        >
          <option value="">All types</option>
          {Object.keys(types).map((key) => (
            <option key={key} value={key}>
              {types[key]}
            </option>
          ))}
        </select>
        <select
          className="form-control"
          value={filterStatus}
          onChange={(e) => setFilterStatus(e.target.value)}
          aria-label="Filter by status"
        >
          <option value="">All statuses</option>
          {Object.keys(statuses).map((key) => (
            <option key={key} value={key}>
              {statuses[key]}
            </option>
          ))}
        </select>
        <input
          className="form-control"
          type="search"
          placeholder="Search"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === "Enter") load();
          }}
          aria-label="Search applications"
        />
        <button type="button" className="btn btn-default" onClick={load}>
          Refresh
        </button>
      </div>

      <div className="apps-admin__layout">
        <section className="apps-admin__panel">
          <h2>Inbox</h2>
          {loading ? (
            <p>Loading…</p>
          ) : items.length === 0 ? (
            <p>No applications found.</p>
          ) : (
            <div className="table-responsive">
              <table className="apps-admin__table">
                <thead>
                  <tr>
                    <th>Entity</th>
                    <th>Applicant</th>
                    <th>Type</th>
                    <th>Status</th>
                    <th>Submitted</th>
                  </tr>
                </thead>
                <tbody>
                  {items.map((item) => (
                    <tr
                      key={item.id}
                      className={
                        "apps-admin__row" +
                        (selected && selected.id === item.id ? " is-active" : "")
                      }
                      onClick={() => selectItem(item)}
                    >
                      <td>{item.name}</td>
                      <td>
                        {item.contact_name}
                        <br />
                        <small>{item.email}</small>
                      </td>
                      <td>{item.type_label || item.type}</td>
                      <td>
                        <span
                          className={
                            "apps-admin__status is-" + (item.status || "")
                          }
                        >
                          {item.status_label || item.status}
                        </span>
                      </td>
                      <td>
                        {item.submitted_at
                          ? String(item.submitted_at).slice(0, 10)
                          : "—"}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </section>

        <section className="apps-admin__panel apps-admin__detail">
          <h2>Detail</h2>
          {!selected ? (
            <p>Select an application to review.</p>
          ) : (
            <>
              <dl>
                <dt>Name</dt>
                <dd>{selected.name}</dd>
                <dt>Type / Status</dt>
                <dd>
                  {selected.type_label} · {selected.status_label}
                </dd>
                <dt>Contact</dt>
                <dd>
                  {selected.contact_name} &lt;{selected.email}&gt;
                </dd>
                <dt>Phone</dt>
                <dd>{selected.phone || "—"}</dd>
                <dt>Website</dt>
                <dd>{selected.website || "—"}</dd>
                <dt>Location</dt>
                <dd>{selected.location || "—"}</dd>
                <dt>Mission</dt>
                <dd>{selected.mission || "—"}</dd>
                <dt>Description</dt>
                <dd>{selected.description || "—"}</dd>
                <dt>Experience</dt>
                <dd>{selected.experience || "—"}</dd>
                <dt>Reason</dt>
                <dd>{selected.reason || "—"}</dd>
                <dt>Notes</dt>
                <dd>{selected.notes || "—"}</dd>
                {selected.created_entity_type ? (
                  <>
                    <dt>Provisioned entity</dt>
                    <dd>
                      {selected.created_entity_type} #
                      {selected.created_entity_id}
                    </dd>
                  </>
                ) : null}
              </dl>

              <label>
                Review notes
                <textarea
                  className="form-control"
                  rows={4}
                  value={reviewNotes}
                  onChange={(e) => setReviewNotes(e.target.value)}
                />
              </label>

              <div className="apps-admin__actions">
                <button
                  type="button"
                  className="btn btn-default"
                  disabled={busy}
                  onClick={() => runAction("save")}
                >
                  Save notes
                </button>
                <button
                  type="button"
                  className="btn btn-success"
                  disabled={busy || !reviewable}
                  onClick={() => {
                    if (
                      window.confirm(
                        "Approve and create the " +
                          (selected.type_label || selected.type) +
                          "?"
                      )
                    ) {
                      runAction("approve");
                    }
                  }}
                >
                  Approve
                </button>
                <button
                  type="button"
                  className="btn btn-info"
                  disabled={busy || !reviewable}
                  onClick={() => runAction("info")}
                >
                  Request info
                </button>
                <button
                  type="button"
                  className="btn btn-danger"
                  disabled={busy || !reviewable}
                  onClick={() => {
                    if (window.confirm("Reject this application?")) {
                      runAction("reject");
                    }
                  }}
                >
                  Reject
                </button>
              </div>
            </>
          )}
        </section>
      </div>
    </div>
  );
}
